// The document a Line Items table exports as (definition.document): a
// purchase quote, a delivery note — the table's lines and totals under a
// letterhead, built in the browser with jsPDF and handed over as a file.
//
// The page, top to bottom: the logo and the company block at the left, the
// title at the right with the number as a Code 128 barcode, the number and
// the date; the two parties (the other side from a field of the work item,
// the ship-to from the definition, or the company again); the table with
// the definition's columns; the totals the table shows. Nothing here is a
// client's — every name and address is data in the definition.
//   · a column with print: false stays on the screen only (received, batch …)
//   · a column's width in % of the table is its width on paper too (a px width
//     is the screen's alone); every text wraps
//   · document.fields: more rows of the work item under the number and the date
//   · document.notes: the words under the totals; document.accent: the colour
//   · every page ends with "<title> <number> · page n of N"

// ── Code 128 (subset B) ───────────────────────────────────────────────────
// Each symbol is six widths (bar, space, bar, space, bar, space) summing to
// 11 modules; the stop symbol is seven widths summing to 13. Value = index.
const CODE128 = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];
const START_B = 104, STOP = 106;

// The bars of a text in Code 128 B: [{ x, w }] in module units, and the
// total width. Characters outside 32–126 are replaced by a space.
export function code128(text) {
  const s = String(text || '');
  const values = [START_B];
  for (const ch of s) { const c = ch.charCodeAt(0); values.push(c >= 32 && c <= 126 ? c - 32 : 0); }
  let sum = START_B;
  for (let i = 1; i < values.length; i++) sum += values[i] * i;
  values.push(sum % 103, STOP);
  const bars = []; let x = 0;
  for (const v of values) {
    const pat = CODE128[v];
    for (let i = 0; i < pat.length; i++) { const w = Number(pat[i]); if (i % 2 === 0) bars.push({ x, w }); x += w; }
  }
  return { bars, width: x, values };
}

// The Code 128 table's own arithmetic, for the tests: every symbol is 11
// modules wide, the stop 13.
export const CODE128_TABLE = CODE128;

// ── the QR link and the signature's place ─────────────────────────────────
// document.qr.url with {key} filled in; a path is made absolute on this site
// (the code is scanned by a phone, which needs the whole address).
export function qrLink(doc, key) {
  const url = doc && doc.qr && doc.qr.url;
  if (!url || !key) return '';
  const filled = String(url).replace(/\{key\}/g, encodeURIComponent(key));
  return /^[a-z][a-z0-9+.-]*:/i.test(filled) ? filled : `${typeof window !== 'undefined' ? window.location.origin : ''}${filled.startsWith('/') ? '' : '/'}${filled}`;
}
// Written once, so the screen and the PDF say the same: the town when the
// browser could name it, and ALWAYS the coordinates (the evidence). '+/-'
// rather than '±': jsPDF's Helvetica cannot draw it.
// No place: why, when the browser said (SignPad keeps { missing }).
const MISSING_TEXT = {
  denied: 'location not captured (blocked in the browser)',
  unavailable: 'location not captured (the device could not find it)',
  timeout: 'location not captured (timed out)',
  unsupported: 'location not captured (not supported by the browser)',
};
export const placeText = (w) => {
  // a document whose signing never asks the place (sign.location: false) says nothing of it
  if (w && w.missing === 'off') return '';
  if (!w || w.lat == null) return (w && MISSING_TEXT[w.missing]) || 'location not captured';
  const named = [w.town, w.postcode, w.country].filter(Boolean).join(', ');
  const coords = `${w.lat}, ${w.lon}${w.acc != null ? ` (+/-${w.acc} m)` : ''}`;
  return named ? `${named} - ${coords}` : coords;
};
export const placeLine = (w) => (placeText(w) ? `Place: ${placeText(w)}` : '');
export const mapsUrl = (w) => (w && w.lat != null ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${w.lat},${w.lon}`)}` : null);

// ── the PDF ──────────────────────────────────────────────────────────────
const M = 14;                        // page margin, mm
const PAGE_W = 210, PAGE_H = 297;
const text = (v) => (v == null ? '' : Array.isArray(v) ? v.map(text).filter(Boolean).join(', ') : typeof v === 'object' ? String(v.value ?? v.name ?? v.displayName ?? v.key ?? '') : String(v));
// "1 706,46": grouped by spaces, a decimal comma — built from en-US, whose
// output is the same in every browser ('en-ZA' is not: Chrome's already
// reads "1 706,46", and swapping its separators again printed "R 124 00").
export const fmtNum = (n, decimals = 2) => (n == null || !Number.isFinite(Number(n)) ? '' : Number(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).replace(/,/g, ' ').replace(/\./g, ','));
const fmtMoney = (n, currency = 'R', decimals = 2) => (n == null || !Number.isFinite(Number(n)) ? '' : `${currency} ${fmtNum(n, decimals)}`);
const fmtDay = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? String(iso || '') : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); };
const rgbOf = (hex, fallback) => { const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || '')); return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : fallback; };
// the columns on paper and their widths (mm): a width in % of the table from the definition, else
// 30 mm for a number and 34 mm for text, as before (a px width is the screen's: a table on a wide
// screen is no guide to A4); the product column (or, without one, the first text column) takes what
// is left — and when the fixed ones are too wide, every column gives a little.
export function paperColumns(columns, width) {
  const cols = (columns || []).filter((c) => c.print !== false);
  const flex = cols.find((c) => c.type === 'product') || cols.find((c) => !NUMERIC.has(c.type)) || cols[0] || null;
  const fixed = (c) => {
    const w = String(c.width || '');
    if (/%$/.test(w)) return (parseFloat(w) / 100) * width;
    return NUMERIC.has(c.type) ? 30 : 34;
  };
  let ws = cols.map((c) => (c === flex ? null : fixed(c)));
  const taken = ws.reduce((a, w) => a + (w || 0), 0);
  const flexW = flex ? Math.max(40, width - taken) : 0;
  ws = ws.map((w) => (w == null ? flexW : w));
  const sum = ws.reduce((a, w) => a + w, 0);
  if (sum > width) ws = ws.map((w) => (w * width) / sum);
  return cols.map((c, i) => ({ col: c, w: ws[i], flex: c === flex }));
}
const NUMERIC = new Set(['number', 'money', 'formula']);
const isMoney = (c) => c.type === 'money' || (c.type === 'formula' && c.format === 'money');

async function loadImage(src) {
  if (!src) return null;
  try {
    const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
    const canvas = document.createElement('canvas'); canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return { dataUrl: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight };
  } catch { return null; }
}

// qrPng: the QR code (base64 PNG) printed in the barcode's place;
// steps + signatures: the signed approval's AUTHORISATION under the totals
// (a box per step: the mark, the name, the role, when, where, the account);
// draft: the order printed before it is signed (marked DRAFT).
// ATS fork (RE-APPLY-ON-SYNC): letterheadStack — the company's name and lines under the logo, as the paper draws them
export async function buildDocumentPdf({ definition: def, lines, totals, issue, fieldByName, logoDataUri, qrPng = null, steps = [], signatures = {}, draft = false, letterheadStack = false }) {
  const doc0 = def.document; if (!doc0) throw new Error('This table has no document to export.');
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const f = issue?.fields || {};
  const valueOf = (name, fallback) => { const fld = fieldByName ? fieldByName(name) : null; const v = fld ? f[fld.id] : undefined; return text(v) || fallback; };
  const number = doc0.numberField ? valueOf(doc0.numberField, issue?.key || '') : (issue?.key || '');
  const date = doc0.dateField ? valueOf(doc0.dateField, f.created) : f.created;
  const party = doc0.party ? valueOf(doc0.party.field, '') : '';
  const company = doc0.company || { name: '', lines: [], website: '' };
  // No shipTo = no ship-to block (a stock pull); shipTo with no lines = us.
  const shipLines = !doc0.shipTo ? [] : doc0.shipTo.lines.length ? doc0.shipTo.lines : [company.name, ...company.lines].filter(Boolean);
  const accent = rgbOf(doc0.accent, [140, 30, 40]);

  // Letterhead
  let y = M;
  const logo = await loadImage(doc0.logo || logoDataUri);
  let x = M;
  // With a company name the letterhead reads beside the logo; without one (a form's
  // document number under its logo) the lines stack under it — as the screen draws it.
  // ATS fork (RE-APPLY-ON-SYNC): letterheadStack stacks a NAMED letterhead too — the name under the
  // logo, then its lines (at the classic size and colour: it is the full letterhead, not a form's note)
  const stack = !company.name || letterheadStack;
  let logoH = 0;
  if (logo) { const h = 14; const w = Math.min(stack ? 70 : 52, (logo.w / logo.h) * h); logoH = Math.min(h, (w * logo.h) / logo.w); pdf.addImage(logo.dataUrl, 'PNG', M, y, w, logoH); if (!stack) x = M + w + 8; }
  const nameY = stack ? y + logoH + 9 : y + 4;
  pdf.setTextColor(...accent); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12);
  if (company.name) pdf.text(company.name, x, nameY);
  const small = stack && !company.name;
  pdf.setTextColor(small ? 90 : 40); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(small ? 8.5 : 9.5);
  let cy = !stack ? y + 9 : company.name ? nameY + 5 : y + logoH + 5;
  // never into the header block at the right (it starts 70 mm from the right edge)
  const coWidth = PAGE_W - M - 74 - x;
  for (const l of [...company.lines, company.website].filter(Boolean)) for (const part of pdf.splitTextToSize(l, coWidth)) { pdf.text(part, x, cy); cy += 4.2; }

  // Title, barcode, number, date — at the right
  const rx = PAGE_W - M;
  pdf.setTextColor(...accent); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(20);
  pdf.text(doc0.title, rx, y + 5, { align: 'right' });
  let ry = y + 12;
  if (qrPng) {
    const qs = 26;
    pdf.addImage(`data:image/png;base64,${qrPng}`, 'PNG', rx - qs, ry - 1, qs, qs);
    ry += qs + 4;
  } else if (doc0.barcode && number) {
    const bc = code128(number); const bw = 70, bh = 11, mod = bw / bc.width;
    pdf.setFillColor(0);
    for (const b of bc.bars) pdf.rect(rx - bw + b.x * mod, ry, b.w * mod, bh, 'F');
    ry += bh + 5;
  }
  pdf.setTextColor(20); pdf.setFontSize(10.5);
  const kv = (k, v) => { pdf.setFont('helvetica', 'bold'); pdf.text(k, rx - 70, ry); pdf.setFont('helvetica', 'normal'); pdf.text(v, rx, ry, { align: 'right' }); ry += 5.5; };
  kv(doc0.numberLabel, number);
  kv(doc0.dateLabel, fmtDay(date));
  // more of the work item: a row each (left out when empty)
  for (const x of doc0.fields || []) {
    const v = valueOf(x.field, '');
    if (!v) continue;
    const shown = /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? fmtDay(v) : String(v);
    // ATS fork (RE-APPLY-ON-SYNC): the same size and spacing as the number and the date above (was 9 pt, 4.8 mm)
    pdf.setFontSize(10.5); pdf.setFont('helvetica', 'bold'); pdf.text(x.label, rx - 70, ry);
    // (the value takes what room the label leaves, so a long label and value never overlap)
    const room = Math.max(15, 70 - pdf.getTextWidth(x.label) - 4);
    pdf.setFont('helvetica', 'normal'); pdf.text(pdf.splitTextToSize(shown, room)[0], rx, ry, { align: 'right' }); ry += 5.5;
  }
  y = Math.max(cy, ry) + 10;

  // Parties (none on a document that names neither)
  const col2 = M + (PAGE_W - 2 * M) / 2;
  if (doc0.party || shipLines.length) {
    pdf.setTextColor(...accent); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
    if (doc0.party) pdf.text(doc0.party.label, M, y);
    if (shipLines.length) pdf.text((doc0.shipTo && doc0.shipTo.label) || 'Ship To Address', col2, y);
    pdf.setTextColor(30); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
    let py = y + 5.5, sy = y + 5.5;
    if (doc0.party) for (const l of pdf.splitTextToSize(party || '—', col2 - M - 8)) { pdf.text(l, M, py); py += 4.6; }
    // the party's other lines (contact number, e-mail), each a field — left out when empty
    if (doc0.party) for (const name of doc0.party.lines || []) { const v = valueOf(name, ''); if (!v) continue; for (const l of pdf.splitTextToSize(String(v), col2 - M - 8)) { pdf.text(l, M, py); py += 4.6; } }
    for (const l of shipLines) { pdf.text(l, col2, sy); sy += 4.6; }
    y = Math.max(py, sy) + 8;
  }

  // The table
  const width = PAGE_W - 2 * M;
  const paper = paperColumns(def.columns, width);
  const xs = []; let cx = M; paper.forEach((p) => { xs.push(cx); cx += p.w; });
  const cellText = (c, l) => {
    if (c.type === 'product') return l.productLabel || l.productKey || '';
    if (NUMERIC.has(c.type)) { const v = l.calc ? l.calc[c.id] : l.vals?.[c.id]; return isMoney(c) ? fmtMoney(v, def.currency, c.decimals ?? 2) : fmtNum(v, c.decimals ?? 0); }
    if (c.type === 'date') { const v = (l.calc && l.calc[c.id]) || l.vals?.[c.id]; return v ? fmtDay(v) : ''; }
    return text(l.vals?.[c.id]);
  };
  const header = () => {
    pdf.setTextColor(30); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
    let h = 1;
    const heads = paper.map((p) => pdf.splitTextToSize(p.col.label, p.w - 3));
    heads.forEach((hl) => { h = Math.max(h, hl.length); });
    paper.forEach((p, i) => { const right = NUMERIC.has(p.col.type); pdf.text(heads[i], right ? xs[i] + p.w - 2 : xs[i] + 2, y, { align: right ? 'right' : 'left' }); });
    y += (h - 1) * 3.8 + 2; pdf.setDrawColor(40); pdf.setLineWidth(0.4); pdf.line(M, y, M + width, y); y += 6;
  };
  header();
  for (const l of lines) {
    // every text cell wraps in its own width, measured in the font it is drawn in (the product
    // bold 10.5, the rest 10 — measured in the header's smaller one, a long name ran into the next
    // column); the row is as tall as its tallest cell
    const cellFont = (p) => { if (p.flex) { pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10.5); } else { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); } };
    const cells = paper.map((p) => {
      const t = cellText(p.col, l);
      if (NUMERIC.has(p.col.type)) return [t];
      cellFont(p);
      return pdf.splitTextToSize(t, Math.max(8, p.w - 4));
    });
    const tall = cells.reduce((a, c) => Math.max(a, c.length), 1);
    const rowH = Math.max(9, tall * 4.6 + 4);
    if (y + rowH > PAGE_H - M - 30) { pdf.addPage(); y = M; header(); }
    paper.forEach((p, i) => {
      const c = p.col; const w = p.w; const right = NUMERIC.has(c.type); const t = cells[i];
      pdf.setTextColor(20);
      cellFont(p);
      if (c.type === 'number' && !isMoney(c)) { const s = t[0] || ''; pdf.setFillColor(235); const tw = pdf.getTextWidth(s) + 5; pdf.roundedRect(xs[i] + w - 2 - tw, y - 4.2, tw, 6, 1.2, 1.2, 'F'); pdf.setTextColor(60); pdf.text(s, xs[i] + w - 4.5, y, { align: 'right' }); }
      else pdf.text(t, right ? xs[i] + w - 2 : xs[i] + 2, y, { align: right ? 'right' : 'left' });
    });
    y += rowH - 3; pdf.setDrawColor(215); pdf.setLineWidth(0.2); pdf.line(M, y, M + width, y); y += 6;
  }

  // Totals
  const shown = (totals || []).filter((t) => t.value != null);
  if (shown.length) {
    if (y + shown.length * 6 + 10 > PAGE_H - M) { pdf.addPage(); y = M; }
    y += 4; const tx = M + width - 70;
    for (const t of shown) {
      if (t.strong) { pdf.setDrawColor(40); pdf.setLineWidth(0.4); pdf.line(tx, y - 4.5, M + width, y - 4.5); y += 2; }
      pdf.setFont('helvetica', t.strong ? 'bold' : 'normal'); pdf.setFontSize(t.strong ? 12 : 10.5); pdf.setTextColor(t.strong ? 20 : 90);
      // A rate set per work item is printed with it: "VAT (0 %)".
      const label = t.editable && t.rate != null ? `${t.label} (${String(t.rate).replace('.', ',')} %)` : t.label;
      pdf.text(label, tx, y); pdf.text(fmtMoney(t.value, def.currency), M + width, y, { align: 'right' }); y += 6.5;
    }
  }

  // The notes: terms, banking details, "received in good order" — under the totals
  if (doc0.notes) {
    const ls = pdf.splitTextToSize(String(doc0.notes), PAGE_W - 2 * M);
    if (y + ls.length * 4.4 + 12 > PAGE_H - M) { pdf.addPage(); y = M; }
    y += 6;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(...accent); pdf.text('NOTES', M, y); y += 4.6;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5); pdf.setTextColor(60); pdf.text(ls, M, y); y += ls.length * 4.4;
  }

  // The Authorisation: the signing steps side by side (two to a row).
  // ATS fork (RE-APPLY-ON-SYNC): a sign-off line per step, not a box — the step's name small and grey,
  // the signature over a rule, the name in bold and the day and time under it (where it was signed,
  // when it was). Nothing signed yet: the empty rule and "Awaiting signature".
  if (steps && steps.length) {
    const cols = Math.min(2, steps.length); const gap = 10;
    const slotW = (PAGE_W - 2 * M - gap * (cols - 1)) / cols; const slotH = 36;
    const rows = Math.ceil(steps.length / cols);
    if (y + 16 + rows * (slotH + 4) > PAGE_H - M) { pdf.addPage(); y = M; }
    y += 8;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(110); pdf.text('AUTHORISATION', M, y); y += 2.5;
    pdf.setDrawColor(225); pdf.setLineWidth(0.2); pdf.line(M, y, PAGE_W - M, y); y += 6;
    for (let i = 0; i < steps.length; i++) {
      const st = steps[i]; const s = signatures && signatures[st.id];
      const x = M + (i % cols) * (slotW + gap); let ty = y + Math.floor(i / cols) * (slotH + 4);
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(110); pdf.text(String(st.label || '').toUpperCase(), x, ty); ty += 2.5;
      const imgH = 15;
      const img = s ? await loadImage(s.image) : null;
      if (img) { const w = Math.min(slotW * 0.8, (img.w / img.h) * imgH); pdf.addImage(img.dataUrl, 'PNG', x, ty, w, imgH); }
      ty += imgH + 1.5;
      pdf.setDrawColor(180, 184, 190); pdf.setLineWidth(0.3); pdf.line(x, ty, x + slotW * 0.8, ty); ty += 4.5;
      if (s) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(20); pdf.text(String(s.name || '—'), x, ty); ty += 4.2;
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(100);
        const when = new Date(s.when);
        if (!Number.isNaN(when.getTime())) { pdf.text(when.toLocaleString('en-GB'), x, ty); ty += 3.6; }
        const url = mapsUrl(s.where);
        const place = placeLine(s.where) ? pdf.splitTextToSize(placeLine(s.where), slotW)[0] : '';
        if (place) { if (url) pdf.textWithLink(place, x, ty, { url }); else pdf.text(place, x, ty); ty += 3.6; }
        if (s.attached) { pdf.setFontSize(7); pdf.setTextColor(130); pdf.text('Signed with an attached image', x, ty); }
      } else {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(140); pdf.text('Awaiting signature', x, ty);
      }
    }
    y += rows * (slotH + 4);
  }
  // The order printed before it is signed: plainly a draft.
  if (draft) {
    const pages = pdf.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      pdf.setPage(p); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.setTextColor(200, 120, 20);
      pdf.text('DRAFT - NOT YET SIGNED', PAGE_W / 2, M - 6, { align: 'center' });
    }
    pdf.setPage(pages);
  }

  const pages = pdf.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    pdf.setPage(p); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(140);
    pdf.text(`${doc0.title} ${number || ''} · page ${p} of ${pages}`.replace(/\s+·/, ' ·'), PAGE_W / 2, PAGE_H - 7, { align: 'center' });
  }
  pdf.setPage(pages);
  const name = `${doc0.fileStem}-${(number || issue?.key || 'document').replace(/[^\w.-]+/g, '_')}.pdf`;
  // save: false — nothing is downloaded; → { name, blob } (many documents in one download). Read here, not in
  // the parameters: a fork that wraps this builder keeps its own first line, and the two merge as they are
  const opts = arguments[0]; // eslint-disable-line prefer-rest-params
  if (opts && opts.save === false) return { name, blob: pdf.output('blob') };
  pdf.save(name);
  return name;
}
